import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/Checkbox'
import { Skeleton } from '@/components/ui/Skeleton'
import { classesQuery } from '@/features/academic/api'
import { cn } from '@/utils/cn'

interface ClassChecklistProps {
  value: number[]
  onChange: (ids: number[]) => void
  labelledBy: string
  invalid?: boolean
}

/** Multi-select of classes as an accessible checkbox list (with select all). */
export function ClassChecklist({ value, onChange, labelledBy, invalid }: ClassChecklistProps) {
  const { t } = useTranslation('announcements')
  const classes = useQuery(classesQuery())
  if (classes.isPending) return <Skeleton className="h-24 w-full" />
  if (classes.error) return <p className="text-sm text-danger-600">{t('Could not load classes.')}</p>

  const all = classes.data.map((c) => c.id)
  const allChecked = all.length > 0 && all.every((id) => value.includes(id))
  const some = value.length > 0 && !allChecked
  const toggle = (id: number, on: boolean) =>
    onChange(on ? [...new Set([...value, id])] : value.filter((v) => v !== id))

  return (
    <div
      role="group"
      aria-labelledby={labelledBy}
      className={cn(
        'rounded-control border bg-surface-muted/60',
        invalid ? 'border-danger-500' : 'border-line',
      )}
    >
      <label className="flex items-center gap-2.5 border-b border-line px-3 py-2 text-sm font-medium text-ink">
        <Checkbox
          checked={allChecked ? true : some ? 'indeterminate' : false}
          onCheckedChange={(c) => onChange(c === true ? all : [])}
        />
        {t('All classes')}{' '}
        <span className="ml-auto text-xs font-normal text-muted">
          {t('{{count}} selected', { count: value.length })}
        </span>
      </label>
      <ul className="grid max-h-48 scrollbar-thin grid-cols-2 gap-x-3 overflow-y-auto p-2 sm:grid-cols-3">
        {classes.data.map((c) => (
          <li key={c.id}>
            <label className="flex cursor-pointer items-center gap-2.5 rounded-xs px-1.5 py-1.5 text-sm text-ink-soft hover:bg-surface">
              <Checkbox checked={value.includes(c.id)} onCheckedChange={(on) => toggle(c.id, on === true)} />
              <span className="truncate">{c.name}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}
